import { createSelector } from '@ngrx/store';
import { createSlice, PayloadAction } from 'ngrx-slice';
import { createSliceEntityAdapter, EntityState } from 'ngrx-slice/entity';

import {
  CollectionPackagingUnitCreateRequest,
  CollectionPackagingUnitHierarchyDto,
  CollectionPackagingUnitUpdateRequest,
  CollectionPackagingUnitUpdateResponse,
  PackageSelectItemDto,
} from '@app/api/marketplace/models';
import { ApiErrorResponse } from '@shared/interfaces/api-error-response';

export interface State {
  packagingUnits: Map<string, Array<PackageSelectItemDto>>;
  packagingUnitsWithDepositFee: Map<string, Array<PackageSelectItemDto>>;
  flatCollections: EntityState<
    CollectionPackagingUnitHierarchyDto & { groupIndex: number }
  >;
  collectionsLoading: boolean;
  collectionsError?: ApiErrorResponse;
  removeCreatedUniqueGroupIndex: number;
  scrollTop: number;
}

export const collectionAdapter = createSliceEntityAdapter<
  CollectionPackagingUnitHierarchyDto & { groupIndex: number }
>({
  selectId: (a) => a.id ?? '',
});

export const initialState: State = {
  packagingUnits: new Map<string, Array<PackageSelectItemDto>>(),
  packagingUnitsWithDepositFee: new Map<string, Array<PackageSelectItemDto>>(),
  flatCollections: collectionAdapter.getInitialState(),
  collectionsLoading: false,
  collectionsError: undefined,
  removeCreatedUniqueGroupIndex: -1,
  scrollTop: 0,
};

export const {
  actions: ProductCollectionsActions,
  selectors,
  ...ProductCollectionsFeature
} = createSlice({
  name: 'product-collections',
  initialState,
  reducers: {
    loadCollections: {
      trigger: (
        state,
        action: PayloadAction<{
          packagingUnitId: string;
        }>
      ) => {
        // collectionAdapter.setAll(state.flatCollections, []);
        state.collectionsLoading = true;
      },
      success: (
        state,
        action: PayloadAction<{
          collections: Array<Array<CollectionPackagingUnitHierarchyDto>>;
        }>
      ) => {
        collectionAdapter.setAll(
          state.flatCollections,
          action.collections.flatMap((collection, index) =>
            collection.map((level) => ({ ...level, groupIndex: index }))
          )
        );
        state.collectionsLoading = false;
      },
      failure: (state, action: PayloadAction<ApiErrorResponse>) => {
        state.collectionsLoading = false;
        state.collectionsError = action;
      },
    },
    saveCollection: {
      trigger: (
        state,
        action: PayloadAction<{
          collectionGroupIndex: number;
          packagingUnitId: string;
          collectionPackagingUnitId: string;
          collectionLevel:
            | CollectionPackagingUnitCreateRequest
            | CollectionPackagingUnitUpdateRequest;
          scrollTop: number;
        }>
      ) => {
        state.collectionsLoading = true;
        state.scrollTop = action.scrollTop;
        state.removeCreatedUniqueGroupIndex = -1;
      },
      success: (
        state,
        action: PayloadAction<{
          collectionGroupIndex: number;
          collectionLevel: CollectionPackagingUnitUpdateResponse;
          created: boolean;
        }>
      ) => {
        if (action.created) {
          collectionAdapter.removeOne(state.flatCollections, '');
          if (!action.collectionLevel.unique) {
            collectionAdapter.addOne(state.flatCollections, {
              ...action.collectionLevel,
              groupIndex: action.collectionGroupIndex,
            });
          } else {
            state.removeCreatedUniqueGroupIndex = action.collectionGroupIndex;
          }
        } else {
          if (!action.collectionLevel.unique) {
            collectionAdapter.upsertOne(state.flatCollections, {
              ...action.collectionLevel,
              groupIndex: action.collectionGroupIndex,
            });
          } else {
            if (action.collectionLevel.id) {
              collectionAdapter.removeOne(
                state.flatCollections,
                action.collectionLevel.id
              );
              state.removeCreatedUniqueGroupIndex = action.collectionGroupIndex;
            }
          }
        }
        state.collectionsLoading = false;
      },
      failure: (state, action: PayloadAction<ApiErrorResponse>) => {
        state.collectionsLoading = false;
        state.collectionsError = action;
      },
    },
    deleteCollection: {
      trigger: (
        state,
        action: PayloadAction<{
          packagingUnitId: string;
          collectionLevelId: string;
        }>
      ) => {
        state.collectionsLoading = true;
      },
      success: (
        state,
        action: PayloadAction<{
          collectionLevelId: string;
        }>
      ) => {
        collectionAdapter.removeOne(
          state.flatCollections,
          action.collectionLevelId
        );
        state.collectionsLoading = false;
      },
      failure: (state, action: PayloadAction<ApiErrorResponse>) => {
        state.collectionsLoading = false;
        state.collectionsError = action;
      },
    },
    loadPackagingUnitsBySubtype: {
      trigger: (
        state,
        action: PayloadAction<{
          withDepositFee: boolean;
          packingMethodSubType: string;
        }>
      ) => {
        // if (action.withDepositFee) {
        //   state.packagingUnitsWithDepositFee.set(
        //     action.packingMethodSubType,
        //     []
        //   );
        // } else {
        //   state.packagingUnits.set(action.packingMethodSubType, []);
        // }
      },
      success: (
        state,
        action: PayloadAction<{
          withDepositFee: boolean;
          packingMethodSubType: string;
          packagingUnits: Array<PackageSelectItemDto>;
        }>
      ) => {
        if (action.withDepositFee) {
          state.packagingUnitsWithDepositFee.set(
            action.packingMethodSubType,
            action.packagingUnits
          );
        } else {
          state.packagingUnits.set(
            action.packingMethodSubType,
            action.packagingUnits
          );
        }
      },
    },
    reset: (_) => initialState,
  },
});

export const ProductCollectionsSelectors = {
  ...selectors,

  selectFlatCollectionList: createSelector(
    selectors.selectFlatCollections,
    (collections) => Object.values(collections.entities)
  ),
  selectPackagingUnitsBySubtype: (
    withDepositFee: boolean,
    packingMethodSubType: string
  ) =>
    createSelector(selectors.selectProductCollectionsState, (state) =>
      withDepositFee
        ? state.packagingUnitsWithDepositFee.get(packingMethodSubType)
        : state.packagingUnits.get(packingMethodSubType)
    ),

  // // TODO,
  // selectCollectionGroups: () =>
  //   createSelector(
  //     ProductCollectionsSelectors.selectFlatCollectionList,
  //     (flatCollections) => {
  //       const collactionGroups = flatCollections.reduce(
  //         (
  //           groups: {
  //             [key: number]: Array<
  //               PackagingUnitInterface & {
  //                 groupIndex: number;
  //               }
  //             >;
  //           },
  //           collectionLevel
  //         ) => {
  //           groups[collectionLevel.groupIndex] =
  //             groups[collectionLevel.groupIndex] ?? [];
  //           groups[collectionLevel.groupIndex].push(collectionLevel);
  //           return groups;
  //         },
  //         {}
  //       );
  //       return Object.values(collactionGroups);
  //     }
  //   ),
};
